import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { statementLayout, unionItems } from '../../utils/reportNormalize';

const HIGHLIGHT = {
  blue: 'bg-blue-50/50 border-blue-200 text-blue-700',
  green: 'bg-green-50/50 border-green-200 text-green-700',
  slate: 'bg-slate-50 border-slate-200 text-slate-700',
};

/**
 * One statement, many columns (months of a trend, or projects). Same sections, breakdown
 * and subtotals as the single-period detailed view, rendered side by side.
 * columns: [{ key, label, sublabel?, data }] where data is the normalized report payload.
 */
export const MultiColumnStatement = ({
  reportType, columns, formatAmount, showGlCodes = false, openDrilldown,
  cogsLabel, netLabel, title, description, icon: Icon, testId = 'multi-column-statement',
}) => {
  const layout = statementLayout(reportType, { cogsLabel, netLabel });
  const colCount = columns.length + (layout.sums ? 1 : 0) + (showGlCodes ? 2 : 1);
  const sumOf = (valueOf) => columns.reduce((s, c) => s + (Number(valueOf(c)) || 0), 0);
  const dense = columns.length > 6;
  const cell = `text-right font-mono ${dense ? 'text-xs px-2' : 'text-sm'} whitespace-nowrap`;
  const neg = (v) => (v < 0 ? 'text-red-500' : '');
  // Fixed column grid so every card's columns line up with each other; wide enough for long amounts.
  const tableCls = 'table-fixed w-full';
  const tableStyle = { minWidth: `${(showGlCodes ? 70 : 0) + 220 + (columns.length + (layout.sums ? 1 : 0)) * (dense ? 150 : 175)}px` };
  const labelCol = <col style={{ width: showGlCodes ? '26%' : '30%' }} />;
  const colGroup = (
    <colgroup>
      {showGlCodes && <col style={{ width: '70px' }} />}
      {labelCol}
      {columns.map((c) => <col key={c.key} />)}
      {layout.sums && <col />}
    </colgroup>
  );
  const onlyHighlights = layout.sections.every((s) => s.highlight);

  const headerRow = (
    <TableRow className="text-xs">
      {showGlCodes && <TableHead>Code</TableHead>}
      <TableHead>Account</TableHead>
      {columns.map((c) => (
        <TableHead key={c.key} className={`text-right whitespace-nowrap ${dense ? 'px-2' : ''}`} data-testid={`mc-col-${c.key}`}>
          <div>{c.label}</div>
          {c.sublabel && !dense && <div className="text-[10px] font-normal text-muted-foreground">{c.sublabel}</div>}
        </TableHead>
      ))}
      {layout.sums && <TableHead className={`text-right font-bold ${dense ? 'px-2' : ''}`}>Total</TableHead>}
    </TableRow>
  );

  const valueCells = (valueOf, cls = '', noSum = false) => (
    <>
      {columns.map((c) => { const v = Number(valueOf(c)) || 0; return <TableCell key={c.key} className={`${cell} ${cls} ${neg(v)}`}>{formatAmount(v)}</TableCell>; })}
      {layout.sums && (
        noSum ? <TableCell /> : (() => { const t = sumOf(valueOf); return <TableCell className={`${cell} font-semibold ${cls} ${neg(t)}`}>{formatAmount(t)}</TableCell>; })()
      )}
    </>
  );

  return (
    <div className="space-y-4" data-testid={testId}>
      {(title || description) && (
        <Card>
          <CardHeader className="pb-3">
            {title && <CardTitle className="flex items-center gap-2 text-lg">{Icon && <Icon className="w-5 h-5 text-[#283F8B]" />}{title}</CardTitle>}
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
        </Card>
      )}
      {onlyHighlights && (
        <Card data-testid="mc-header">
          <CardContent className="py-1 px-0 overflow-x-auto">
            <Table className={tableCls} style={tableStyle}>{colGroup}<TableHeader>{headerRow}</TableHeader></Table>
          </CardContent>
        </Card>
      )}
      {layout.sections.map((sec) => {
        if (sec.highlight) {
          return (
            <Card key={sec.key} className={HIGHLIGHT[sec.highlight]} data-testid={`mc-${sec.key}`}>
              <CardContent className="py-1 px-0 overflow-x-auto">
                <Table className={tableCls} style={tableStyle}>
                  {colGroup}
                  <TableBody>
                    <TableRow className="hover:bg-transparent border-0">
                      {showGlCodes && <TableCell />}
                      <TableCell className="font-bold">{sec.title}</TableCell>
                      {valueCells((c) => sec.total(c.data), 'font-bold', sec.noSum)}
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        }
        const items = unionItems(columns, sec.items);
        if (sec.optional && !items.length) return null;
        return (
          <Card key={sec.key} data-testid={`mc-${sec.key}`}>
            <CardHeader className="py-3">
              <CardTitle className={`text-base ${sec.color}`}>{sec.title}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <Table className={tableCls} style={tableStyle}>
                {colGroup}
                <TableHeader>{headerRow}</TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.code || it.name} className={openDrilldown ? 'cursor-pointer hover:bg-muted/50' : ''} onClick={() => openDrilldown && openDrilldown({ code: it.code, name: it.name })}>
                      {showGlCodes && <TableCell className="text-muted-foreground font-mono text-xs">{it.code}</TableCell>}
                      <TableCell className="text-sm">{it.name}</TableCell>
                      {valueCells((c) => it.values[c.key] || 0)}
                    </TableRow>
                  ))}
                  {!items.length && (
                    <TableRow><TableCell colSpan={colCount} className="text-sm text-muted-foreground py-3">No items recorded</TableCell></TableRow>
                  )}
                  <TableRow className="bg-muted/30 font-medium">
                    {showGlCodes && <TableCell />}
                    <TableCell>{sec.totalLabel}</TableCell>
                    {valueCells((c) => sec.total(c.data), 'font-medium')}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MultiColumnStatement;
